import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../../lib/api";
import { PageHeader, Card, Table, Td, Badge, Empty, Btn, Modal } from "../../components/UI";
import { ClinicalTabs, useClients } from "./shared";
import { FSelect, FInput } from "../engagement/FormControls";
import { isLateEntry, LateEntryChip } from "../../lib/lateEntry";
import { VersionAccordion } from "../../components/VersionAccordion";
import { ExportCsvBtn } from "../../components/ExportCsvBtn";
import { Plus, Search, Pencil, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";

export default function ProgressNotes() {
  const navigate = useNavigate();
  const clients = useClients();
  const [notes, setNotes] = useState([]);
  const [clientFilter, setClientFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    const params = {};
    if (clientFilter) params.client_id = clientFilter;
    if (statusFilter) params.status = statusFilter;
    if (search) params.search = search;
    api.get("/progress-notes", { params }).then((r) => { setNotes(r.data); setLoading(false); });
  };
  useEffect(load, [clientFilter, statusFilter, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const doDelete = async () => {
    try {
      await api.delete(`/progress-notes/${confirmDelete.id}`);
      toast.success("Progress note deleted");
      setConfirmDelete(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const COL_COUNT = 8; // ver + date + client + provider + format + service + status + actions

  return (
    <div>
      <PageHeader title="Progress Notes" subtitle="SOAP / DAP session documentation"
        action={<div className="flex items-center gap-2"><ExportCsvBtn resource="progress_notes" /><Btn data-testid="add-progress-note-btn" onClick={() => navigate("/clinical/progress-notes/new")}><Plus size={15} /> New Progress Note</Btn></div>} />
      <ClinicalTabs />
      <Card>
        <div className="flex flex-wrap gap-3 p-4">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-3.5 text-[#84675B]" />
            <FInput data-testid="pn-search" className="pl-8 w-60" placeholder="Search client or service…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <FSelect data-testid="pn-client-filter" className="w-56" placeholder="All Clients" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
          </FSelect>
          <FSelect data-testid="pn-status-filter" className="w-44" placeholder="All Statuses" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="draft">Draft</option><option value="completed">Completed</option><option value="pending_cosign">Pending Co-Sign</option><option value="signed">Signed</option>
          </FSelect>
        </div>
        <Table headers={["", "Date", "Client", "Provider", "Format", "Service", "Status", ""]}>
          {notes.map((n) => (
            <VersionAccordion key={n.id} record={n} kind="progress_note" colSpan={COL_COUNT}
              primaryTestId={`pn-row-${n.id}`}
              rowClassName="hover:bg-[#FDFCF8] transition-colors"
              renderPrimary={({ expandToggle, versionBadge }) => (
                <>
                  <Td className="w-8">{expandToggle}</Td>
                  <Td className="cursor-pointer" onClick={() => navigate(`/clinical/progress-notes/${n.id}`)}>
                    <span className="flex items-center gap-1.5">
                      {dayjs(n.date).format("MM/DD/YYYY")} {n.time}
                      {isLateEntry(n.date, n.created_at) && <LateEntryChip id={n.id} />}
                      {versionBadge}
                    </span>
                  </Td>
                  <Td className="font-medium text-[#3E2723] cursor-pointer" onClick={() => navigate(`/clinical/progress-notes/${n.id}`)}>{n.client_name}</Td>
                  <Td>{n.provider_name}</Td>
                  <Td><span className="rounded-full bg-[#EDE7F6] text-[#5E35B1] px-2.5 py-0.5 text-xs font-bold">{n.format}</span></Td>
                  <Td>{n.service || "-"}</Td>
                  <Td><Badge value={n.status} /></Td>
                  <Td>
                    <div className="flex gap-2 justify-end">
                      <button type="button" data-testid={`pn-open-${n.id}`} aria-label="Open" onClick={() => navigate(`/clinical/progress-notes/${n.id}`)}
                        className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]">
                        {["signed", "pending_cosign"].includes(n.status) ? <Lock size={13} /> : <Pencil size={13} />}
                      </button>
                      {!["signed", "pending_cosign"].includes(n.status) && (
                        <button type="button" data-testid={`pn-delete-${n.id}`} aria-label="Delete" onClick={() => setConfirmDelete(n)}
                          className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#FFEBEE] hover:text-[#C62828]"><Trash2 size={13} /></button>
                      )}
                    </div>
                  </Td>
                </>
              )}
            />
          ))}
        </Table>
        {!loading && notes.length === 0 && <Empty text="No progress notes yet - create the first one" />}
      </Card>

      <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Delete Progress Note">
        <p className="text-sm text-[#6D4C41] mb-5">Delete the {confirmDelete?.format} note for <span className="font-semibold">{confirmDelete?.client_name}</span> on {confirmDelete && dayjs(confirmDelete.date).format("MM/DD/YYYY")}?</p>
        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={() => setConfirmDelete(null)}>Cancel</Btn>
          <Btn data-testid="pn-confirm-delete-btn" variant="danger" onClick={doDelete}>Delete</Btn>
        </div>
      </Modal>
    </div>
  );
}
